// Card value system — rank multiplier × type multiplier

// MLB Top 100 players (2025-26) — real rankings by talent/value
const playerRanks = {
  // Top 10 — Superstars (×10)
  'Shohei Ohtani': 1,
  'Aaron Judge': 2,
  'Mookie Betts': 3,
  'Juan Soto': 4,
  'Trea Turner': 5,
  'Ronald Acuña Jr.': 6,
  'Freddie Freeman': 7,
  'Corey Seager': 8,
  'Bobby Witt Jr.': 9,
  'Julio Rodríguez': 10,
  // 11-25 — Stars (×7)
  'Corbin Carroll': 11,
  'Marcus Semien': 12,
  'Fernando Tatis Jr.': 13,
  'Gunnar Henderson': 14,
  'Elly De La Cruz': 15,
  'Jose Ramirez': 16,
  'Wander Franco': 17,
  'Adley Rutschman': 18,
  'Yordan Alvarez': 19,
  'Kyle Tucker': 20,
  'Matt Olson': 21,
  'Bryce Harper': 22,
  'Francisco Lindor': 23,
  'Rafael Devers': 24,
  'Vladimir Guerrero Jr.': 25,
  // 26-50 — Elite (×5)
  'Mike Trout': 26,
  'Pete Alonso': 27,
  'CJ Abrams': 28,
  'Jackson Chourio': 29,
  'Marcell Ozuna': 30,
  'Spencer Torkelson': 31,
  'William Contreras': 32,
  'Bo Bichette': 33,
  'Alex Bregman': 34,
  'Ozzie Albies': 35,
  'Jarren Duran': 36,
  'Jackson Merrill': 37,
  'Evan Carter': 38,
  'Jordan Walker': 39,
  'Colton Cowser': 40,
  'Zack Gelof': 41,
  'James Outman': 42,
  'Masataka Yoshida': 43,
  'Anthony Volpe': 44,
  'Dansby Swanson': 45,
  'Manny Machado': 46,
  'J.P. Crawford': 47,
  'Ke\'Bryan Hayes': 48,
  'Christian Yelich': 49,
  'Jazz Chisholm Jr.': 50,
  // 51-75 — Top Pitchers + Very Good (×3)
  'Gerrit Cole': 51,
  'Spencer Strider': 52,
  'Zack Wheeler': 53,
  'Corbin Burnes': 54,
  'Logan Webb': 55,
  'Tarik Skubal': 56,
  'Dylan Cease': 57,
  'Yoshinobu Yamamoto': 58,
  'Paul Skenes': 59,
  'Framber Valdez': 60,
  'Kevin Gausman': 61,
  'Tyler Glasnow': 62,
  'Hunter Brown': 63,
  'Grayson Rodriguez': 64,
  'Max Fried': 65,
  'Justin Verlander': 66,
  'Chris Sale': 67,
  'Ranger Suarez': 68,
  'Luis Castillo': 69,
  'Blake Snell': 70,
  'Shane McClanahan': 71,
  'Kodai Senga': 72,
  'Pablo Lopez': 73,
  'Joe Ryan': 74,
  'Logan Gilbert': 75,
  // 76-100 — Very Good / Top Relievers (×2)
  'Sonny Gray': 76,
  'Aaron Nola': 77,
  'Seth Lugo': 78,
  'Bryce Miller': 79,
  'MacKenzie Gore': 80,
  'Seiya Suzuki': 81,
  'Willson Contreras': 82,
  'Austin Riley': 83,
  'Cody Bellinger': 84,
  'Xander Bogaerts': 85,
  'Tommy Edman': 86,
  'Luis Robert Jr.': 87,
  'Byron Buxton': 88,
  'Willy Adames': 89,
  'Isaac Paredes': 90,
  'Josh Jung': 91,
  'Riley Greene': 92,
  'Kerry Carpenter': 93,
  'Emmanuel Clase': 94,
  'Ryan Helsley': 95,
  'Josh Hader': 96,
  'Devin Williams': 97,
  'Edwin Diaz': 98,
  'Robert Suarez': 99,
  'Andres Munoz': 100,
};

// Rank → multiplier
const getRankMultiplier = (rank) => {
  if (!rank) return 1;
  if (rank <= 10) return 10;
  if (rank <= 25) return 7;
  if (rank <= 50) return 5;
  if (rank <= 75) return 3;
  if (rank <= 100) return 2;
  return 1;
};

// Card type → multiplier
const typeMultipliers = {
  base: 1,
  rare_999: 10,
  rare_dual: 25,
  rare_patch: 75,
  rare_auto: 150,
  auto_patch: 400,
  chrome_auto: 250,
  patch_auto: 750,
  dual_patch: 1500,
};

// Signature premium — certain players' autographs are worth more
// Multiplied on top of type multiplier for auto/patch_auto cards
const signaturePremium = {
  'Shohei Ohtani': 5,     // Ohtani auto is his rookie card — massive premium
  'Aaron Judge': 3,
  'Mookie Betts': 2,
  'Juan Soto': 2,
  'Vladimir Guerrero Jr.': 2,
  'Paul Skenes': 2,
  'Elly De La Cruz': 2,
};

const isAutoType = (cardType) => cardType === 'rare_auto' || cardType === 'auto_patch' || cardType === 'chrome_auto' || cardType === 'patch_auto';

// Jersey number match — check if numbering matches player's jersey number
const getJerseyMatch = (cardData, numbering) => {
  if (!numbering) return 1;
  const pullNum = parseInt(numbering.split('/')[0], 10);
  if (isNaN(pullNum)) return 1;
  const jerseyNum = parseInt(cardData?.number, 10);
  if (isNaN(jerseyNum)) return 1;
  if (pullNum === jerseyNum) return 3; // 3x for jersey match!
  return 1;
};

export const isJerseyMatch = (cardData, numbering) => {
  if (!numbering) return false;
  const pullNum = parseInt(numbering.split('/')[0], 10);
  const jerseyNum = parseInt(cardData?.number, 10);
  return !isNaN(pullNum) && !isNaN(jerseyNum) && pullNum === jerseyNum;
};

export const calculateCardValue = (cardData, cardType, numbering) => {
  const name = cardData?.name || cardData?.player1?.name || '';
  const rank = playerRanks[name];
  const rankMult = getRankMultiplier(rank);
  const typeMult = typeMultipliers[cardType] || 1;

  // For dual cards, multiply both players' rank multipliers together
  if (cardData?.player1 && cardData?.player2) {
    const jerseyMult = getJerseyMatch(cardData.player1, numbering) > 1 ? 3 : getJerseyMatch(cardData.player2, numbering);
    const rank1 = playerRanks[cardData.player1.name];
    const rank2 = playerRanks[cardData.player2.name];
    const mult1 = getRankMultiplier(rank1);
    const mult2 = getRankMultiplier(rank2);
    return 1 * mult1 * mult2 * typeMult * jerseyMult;
  }

  // Signature premium for auto cards
  let sigPremium = 1;
  if (isAutoType(cardType)) {
    sigPremium = signaturePremium[name] || 1;
  }

  // Rookie premium — card with a different team than current (e.g. Ohtani on LAA)
  let rookiePremium = 1;
  if (cardData?.team === 'LAA' && name === 'Shohei Ohtani') rookiePremium = 3;

  // Jersey number match
  const jerseyMult = getJerseyMatch(cardData, numbering);

  // 2-color patch premium (1.5x)
  const patchPremium = cardData?.patchColor2 && cardData?.patchVariant !== 3 ? 1.5 : 1;

  return 1 * rankMult * typeMult * sigPremium * rookiePremium * jerseyMult * patchPremium;
};

// Daily market multiplier — deterministic from date so all users see the same value
// Uses a seeded PRNG (mulberry32) for good distribution
const mulberry32 = (seed) => {
  let t = seed + 0x6D2B79F5;
  t = Math.imul(t ^ t >>> 15, t | 1);
  t ^= t + Math.imul(t ^ t >>> 7, t | 61);
  return ((t ^ t >>> 14) >>> 0) / 4294967296;
};

export const getDailyMarket = (date = new Date()) => {
  // Create a unique seed from the date — day of year * year gives unique per-day values
  const year = date.getFullYear();
  const start = new Date(year, 0, 0);
  const dayOfYear = Math.floor((date - start) / 86400000);
  const seed = year * 367 + dayOfYear;
  const random = mulberry32(seed);
  // Map to 0.82 — 1.28 range (realistic daily market swings)
  const multiplier = 0.82 + random * 0.46;
  return Math.round(multiplier * 100) / 100;
};

export const getMarketLabel = (mult) => {
  if (mult >= 1.25) return { label: 'Hot', color: '#22c55e' };
  if (mult >= 1.05) return { label: 'Up', color: '#86efac' };
  if (mult >= 0.95) return { label: 'Stable', color: '#999' };
  if (mult >= 0.80) return { label: 'Down', color: '#f59e0b' };
  return { label: 'Cold', color: '#ef4444' };
};

export const applyMarket = (baseValue, mult) => {
  return Math.round(baseValue * mult);
};

export const formatValue = (value) => {
  if (value >= 1000) return `$${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`;
  return `$${value}`;
};
